import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LeaningVideo from '@/components/organisms/Learning/Video';
import LeaningFlashCard from '@/components/organisms/Learning/FlashCard';
import SpellCheck from '@/components/organisms/Learning/SpellCheck';
import AlphabetChoice from '@/components/organisms/Learning/AlphabetChoice';
import DragDrap from '@/components/organisms/Learning/DragMatch';
import WordChoice from '@/components/organisms/Learning/WordChoice';
import PartWriting from '@/components/organisms/Learning/PartWriting';
import Diagram from '@/components/organisms/Learning/Diagram';
import Dictation from '@/components/organisms/Learning/Dictation';
import FillBlank from '@/components/organisms/Learning/FillBlank';
import EngWritng from '@/components/organisms/Learning/EngWritng';
import FreeWritng from '@/components/organisms/Learning/FreeWritng';
import StoryDialog from '@/components/organisms/Learning/StoryDialog';
import StoryDialogQustion from '@/components/organisms/Learning/StoryDialogQustion';
import Complete from '@/components/organisms/Learning/Complete';
import { Button } from '@/components/atoms/Button/Button';
import { academyTextbookApi } from '@/services/api';
import { getSlugPrefix } from '@/utils/slug';

type Activity = {
  id: number;
  activityType: string;
  title: string;
  instructions: string;
  configJson: string;
  orderIndex: number;
  items: any[];
};

type UnitPreview = {
  unitId: number;
  unitCode: string;
  unitTitle: string;
  unitType: string;
  unitCategory: string;
  textbookTitle?: string;
  textbookId?: number;
  activities: Activity[];
  totalActivities: number;
  nextUnitId?: number;
  nextUnitTitle?: string;
};

type FlatItem = {
  activity: Activity;
  item: any;
  globalIndex: number;
  activityIndex: number;
  itemIndex: number;
};

const ActivityComponents: Record<string, React.ComponentType<any>> = {
  VIDEO_QUIZ: LeaningVideo,
  DIAGRAM: Diagram,
  DICTATION: Dictation,
  FILL_BLANK: FillBlank,
  SPELL_CHECK: SpellCheck,
  ALPHABET_CHOICE: AlphabetChoice,
  FLASH_CARD: LeaningFlashCard,
  STORY_DIALOG: StoryDialog,
  STORY_DIALOG_QA: StoryDialogQustion,
  WORD_CHOICE: WordChoice,
  DRAG_MATCH: DragDrap,
  FREE_WRITING: FreeWritng,
  ENG_WRITING: EngWritng,
  PART_WRITING: PartWriting,
};

const UNIT_TYPE_CONFIG: Record<string, {
  allowedDbActivities: string[];
  flashCardExtensions?: string[];
  wordChoiceExtensions?: string[];
  dragMatchMaxItems?: number | null;
}> = {
  PHONICS_DAY: {
    allowedDbActivities: ['DICTATION', 'FLASH_CARD'],
    flashCardExtensions: ['DRAG_MATCH', 'SPELL_CHECK', 'ALPHABET_CHOICE'],
    dragMatchMaxItems: null,
  },
  STARTER_PHONICS: {
    allowedDbActivities: ['DICTATION', 'FLASH_CARD'],
    flashCardExtensions: ['DRAG_MATCH', 'SPELL_CHECK'],
    dragMatchMaxItems: null,
  },
  STARTER_NUANCE: {
    allowedDbActivities: ['FLASH_CARD', 'WORD_CHOICE', 'DICTATION'],
    flashCardExtensions: ['DRAG_MATCH'],
    wordChoiceExtensions: ['PART_WRITING'],
    dragMatchMaxItems: 6,
  },
  STARTER_STRUCTURE: {
    allowedDbActivities: ['DICTATION', 'FLASH_CARD', 'DIAGRAM'],
    flashCardExtensions: ['DRAG_MATCH'],
    wordChoiceExtensions: ['PART_WRITING'],
    dragMatchMaxItems: 6,
  },
  ESSAY: {
    allowedDbActivities: ['DICTATION', 'WORD_CHOICE', 'FLASH_CARD', 'PART_WRITING', 'ENG_WRITING', 'FREE_WRITING'],
    dragMatchMaxItems: 6,
  },
  TRANSLATION: {
    allowedDbActivities: ['DICTATION', 'WORD_CHOICE', 'FLASH_CARD', 'PART_WRITING', 'FILL_BLANK', 'ENG_WRITING', 'FREE_WRITING'],
    dragMatchMaxItems: 6,
  },
  READING_BOOK: {
    allowedDbActivities: ['FLASH_CARD', 'STORY_DIALOG', 'STORY_DIALOG_QA'],
    flashCardExtensions: ['DRAG_MATCH'],
    dragMatchMaxItems: null,
  },
};

const SINGLE_ITEM_ACTIVITIES = [
  'DRAG_MATCH',
  'SPELL_CHECK',
  'ALPHABET_CHOICE',
  'PART_WRITING',
  'STORY_DIALOG',
  'STORY_DIALOG_QA',
  'VIDEO_QUIZ',
  'DIAGRAM',
  'DICTATION',
  'FILL_BLANK',
  'ENG_WRITING',
  'FREE_WRITING',
];

const buildFlatItems = (unitData: UnitPreview | null): FlatItem[] => {
  if (!unitData) return [];
  const activities = unitData.activities || [];
  const unitType = unitData.unitType;
  const config = UNIT_TYPE_CONFIG[unitType];
  if (!config) return [];

  const filteredActivities = activities.filter((activity) => config.allowedDbActivities.includes(activity.activityType));
  const expandedActivities: Activity[] = [];

  filteredActivities.forEach((activity) => {
    // ESSAY/TRANSLATION: FLASH_CARD -> DICTATION & DRAG_MATCH
    if ((unitType === 'ESSAY' || unitType === 'TRANSLATION') && activity.activityType === 'FLASH_CARD') {
      activity.items.forEach((flashCardItem, idx) => {
        expandedActivities.push({
          ...activity,
          id: activity.id + Math.random(),
          activityType: 'DICTATION',
          title: '딕테이션',
          items: [{
            id: flashCardItem.id,
            itemType: 'DICTATION',
            orderIndex: idx,
            refType: 'FLASH_CARD',
            refId: flashCardItem.id,
            contentJson: flashCardItem.contentJson,
            answerJson: null,
            score: null,
          }],
        });
      });

      expandedActivities.push({
        ...activity,
        id: activity.id + 1000,
        activityType: 'DRAG_MATCH',
        title: '드래그 매치',
        items: [{
          id: activity.id,
          itemType: 'DRAG_MATCH',
          orderIndex: 0,
          refType: 'FLASH_CARD',
          refId: activity.id,
          contentJson: JSON.stringify({ flashCards: activity.items }),
          answerJson: null,
          score: null,
        }],
      });
      return;
    }

    // DICTATION video quiz 변환
    if (activity.activityType === 'DICTATION') {
      try {
        const activityConfig = JSON.parse(activity.configJson || '{}');
        if (activityConfig.videoQuizEnabled === true || !!activityConfig.videoUrl) {
          const videoQuizItems = activity.items && activity.items.length > 0
            ? activity.items
            : [{
              id: activity.id,
              itemType: 'VIDEO_QUIZ',
              orderIndex: 0,
              refType: 'NONE',
              refId: null,
              contentJson: activity.configJson,
              answerJson: null,
              score: null,
            }];

          expandedActivities.push({
            ...activity,
            activityType: 'VIDEO_QUIZ',
            title: activity.title || '동영상 퀴즈',
            items: videoQuizItems,
          });
          return;
        }
      } catch {
        // ignore parse error
      }
    }

    if (activity.activityType === 'STORY_DIALOG') {
      expandedActivities.push({ ...activity });
      return;
    }

    if (activity.activityType === 'STORY_DIALOG_QA') {
      expandedActivities.push({ ...activity });
      return;
    }

    expandedActivities.push(activity);

    if (activity.activityType === 'WORD_CHOICE' && config.wordChoiceExtensions) {
      config.wordChoiceExtensions.forEach((extensionType) => {
        if (extensionType === 'PART_WRITING' && activity.items?.length) {
          activity.items.forEach((wcItem: any, wcIdx: number) => {
            let perItemTitleTranslation = '';
            try {
              const wcContent = wcItem?.contentJson ? (typeof wcItem.contentJson === 'string' ? JSON.parse(wcItem.contentJson) : wcItem.contentJson) : {};
              perItemTitleTranslation = wcContent?.titleTranslation || '';
            } catch (e) {
              console.error('wcItem contentJson parse error:', e);
            }
            let parentConfigJson = activity.configJson;
            try {
              const parsedCfg = activity.configJson ? (typeof activity.configJson === 'string' ? JSON.parse(activity.configJson) : activity.configJson) : {};
              parentConfigJson = JSON.stringify({ ...parsedCfg, titleTranslation: perItemTitleTranslation || parsedCfg.titleTranslation });
            } catch (e) {
              console.error('WORD_CHOICE parent config parse error:', e);
            }
            expandedActivities.push({
              ...activity,
              id: activity.id + Math.random(),
              activityType: extensionType,
              title: '부분 영작',
              items: [{
                id: wcItem.id,
                itemType: extensionType,
                orderIndex: wcIdx,
                refType: 'WORD_CHOICE',
                refId: wcItem.id,
                contentJson: JSON.stringify({
                  wordChoices: [wcItem],
                  parentConfigJson,
                }),
                answerJson: null,
                score: null,
              }],
            });
          });
          return;
        }
        expandedActivities.push({
          ...activity,
          id: activity.id + Math.random(),
          activityType: extensionType,
          title: extensionType === 'PART_WRITING' ? '부분 영작' : extensionType,
          items: [{
            id: activity.id,
            itemType: extensionType,
            orderIndex: 0,
            refType: 'WORD_CHOICE',
            refId: activity.id,
            contentJson: JSON.stringify({
              wordChoices: activity.items,
              parentConfigJson: activity.configJson,
            }),
            answerJson: null,
            score: null,
          }],
        });
      });
    }
  });

  // WORD_CHOICE 확장 전용 (필터링에서 제외된 경우)
  if (config.wordChoiceExtensions && config.wordChoiceExtensions.length > 0) {
    const filteredWordChoiceIds = new Set(
      filteredActivities
        .filter((act) => act.activityType === 'WORD_CHOICE')
        .map((act) => act.id),
    );

    const notFilteredWordChoiceActivities = activities.filter(
      (act) => act.activityType === 'WORD_CHOICE' && !filteredWordChoiceIds.has(act.id),
    );

    notFilteredWordChoiceActivities.forEach((activity) => {
      config.wordChoiceExtensions!.forEach((extensionType) => {
        if (extensionType === 'PART_WRITING' && activity.items?.length) {
          activity.items.forEach((wcItem: any, wcIdx: number) => {
            let perItemTitleTranslation = '';
            try {
              const wcContent = wcItem?.contentJson ? (typeof wcItem.contentJson === 'string' ? JSON.parse(wcItem.contentJson) : wcItem.contentJson) : {};
              perItemTitleTranslation = wcContent?.titleTranslation || '';
            } catch (e) {
              console.error('wcItem contentJson parse error:', e);
            }
            let parentConfigJson = activity.configJson;
            try {
              const parsedCfg = activity.configJson ? (typeof activity.configJson === 'string' ? JSON.parse(activity.configJson) : activity.configJson) : {};
              parentConfigJson = JSON.stringify({ ...parsedCfg, titleTranslation: perItemTitleTranslation || parsedCfg.titleTranslation });
            } catch (e) {
              console.error('WORD_CHOICE parent config parse error:', e);
            }
            expandedActivities.push({
              ...activity,
              id: activity.id + Math.random(),
              activityType: extensionType,
              title: '부분 영작',
              items: [{
                id: wcItem.id,
                itemType: extensionType,
                orderIndex: wcIdx,
                refType: 'WORD_CHOICE',
                refId: wcItem.id,
                contentJson: JSON.stringify({
                  wordChoices: [wcItem],
                  parentConfigJson,
                }),
                answerJson: null,
                score: null,
              }],
            });
          });
          return;
        }
        expandedActivities.push({
          ...activity,
          id: activity.id + Math.random(),
          activityType: extensionType,
          title: extensionType === 'PART_WRITING' ? '부분 영작' : extensionType,
          items: [{
            id: activity.id,
            itemType: extensionType,
            orderIndex: 0,
            refType: 'WORD_CHOICE',
            refId: activity.id,
            contentJson: JSON.stringify({
              wordChoices: activity.items,
              parentConfigJson: activity.configJson,
            }),
            answerJson: null,
            score: null,
          }],
        });
      });
    });
  }

  // FLASH_CARD 확장
  if (config.flashCardExtensions && config.flashCardExtensions.length > 0 && unitType !== 'ESSAY' && unitType !== 'TRANSLATION') {
    const allFlashCardActivities = activities.filter((act) => act.activityType === 'FLASH_CARD');
    allFlashCardActivities.forEach((activity) => {
      config.flashCardExtensions!.forEach((extensionType) => {
        const shouldCreateIndividually = extensionType === 'SPELL_CHECK' || extensionType === 'ALPHABET_CHOICE';
        if (shouldCreateIndividually) {
          activity.items.forEach((flashCardItem, idx) => {
            expandedActivities.push({
              ...activity,
              id: activity.id + Math.random(),
              activityType: extensionType,
              title: extensionType === 'SPELL_CHECK' ? '스펠 체크' : extensionType === 'ALPHABET_CHOICE' ? '알파벳 고르기' : extensionType,
              items: [{
                id: flashCardItem.id,
                itemType: extensionType,
                orderIndex: idx,
                refType: 'FLASH_CARD',
                refId: flashCardItem.id,
                contentJson: flashCardItem.contentJson,
                answerJson: null,
                score: null,
              }],
            });
          });
        } else {
          expandedActivities.push({
            ...activity,
            id: activity.id + Math.random(),
            activityType: extensionType,
            title: extensionType === 'DRAG_MATCH' ? '드래그 매치' : extensionType,
            items: [{
              id: activity.id,
              itemType: extensionType,
              orderIndex: 0,
              refType: 'FLASH_CARD',
              refId: activity.id,
              contentJson: JSON.stringify({ flashCards: activity.items }),
              answerJson: null,
              score: null,
            }],
          });
        }
      });
    });
  }

  // ENG_WRITING 확장 (ESSAY/TRANSLATION)
  if ((unitType === 'ESSAY' || unitType === 'TRANSLATION') && config.allowedDbActivities.includes('ENG_WRITING')) {
    const partWritingActivities = expandedActivities.filter((act) => act.activityType === 'PART_WRITING');
    partWritingActivities.forEach((activity) => {
      expandedActivities.push({
        ...activity,
        id: activity.id + 1000,
        activityType: 'ENG_WRITING',
        title: '영작',
        items: [],
      });
    });
  }

  if (unitType === 'ESSAY' || unitType === 'TRANSLATION') {
    const activityOrder = ['VIDEO_QUIZ', 'DICTATION', 'DRAG_MATCH', 'PART_WRITING', 'FILL_BLANK', 'ENG_WRITING', 'FREE_WRITING'];
    expandedActivities.sort((a, b) => {
      const aIndex = activityOrder.indexOf(a.activityType);
      const bIndex = activityOrder.indexOf(b.activityType);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      if (aIndex !== bIndex) return aIndex - bIndex;
      return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
    });
  } else if (unitType === 'STARTER_NUANCE') {
    const activityOrder = ['VIDEO_QUIZ', 'FLASH_CARD', 'DRAG_MATCH', 'WORD_CHOICE', 'PART_WRITING'];
    expandedActivities.sort((a, b) => {
      const aIndex = activityOrder.indexOf(a.activityType);
      const bIndex = activityOrder.indexOf(b.activityType);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      if (aIndex !== bIndex) return aIndex - bIndex;
      return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
    });
  } else if (unitType === 'STARTER_STRUCTURE') {
    const activityOrder = ['VIDEO_QUIZ', 'FLASH_CARD', 'DRAG_MATCH', 'DIAGRAM', 'PART_WRITING'];
    expandedActivities.sort((a, b) => {
      const aIndex = activityOrder.indexOf(a.activityType);
      const bIndex = activityOrder.indexOf(b.activityType);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      if (aIndex !== bIndex) return aIndex - bIndex;
      return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
    });
  } else if (unitType === 'READING_BOOK') {
    const activityOrder = ['FLASH_CARD', 'DRAG_MATCH', 'STORY_DIALOG', 'STORY_DIALOG_QA'];
    expandedActivities.sort((a, b) => {
      const aIndex = activityOrder.indexOf(a.activityType);
      const bIndex = activityOrder.indexOf(b.activityType);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      if (aIndex !== bIndex) return aIndex - bIndex;
      return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
    });
  } else {
    const videoQuizIndex = expandedActivities.findIndex((act) => act.activityType === 'VIDEO_QUIZ');
    if (videoQuizIndex > 0) {
      const videoQuiz = expandedActivities.splice(videoQuizIndex, 1)[0];
      expandedActivities.unshift(videoQuiz);
    }
  }

  const flatItems: FlatItem[] = [];
  expandedActivities.forEach((activity, activityIndex) => {
    if (SINGLE_ITEM_ACTIVITIES.includes(activity.activityType)) {
      flatItems.push({
        activity,
        item: activity.items?.[0] || {},
        globalIndex: flatItems.length,
        activityIndex,
        itemIndex: 0,
      });
    } else {
      (activity.items || []).forEach((item, itemIndex) => {
        flatItems.push({
          activity,
          item,
          globalIndex: flatItems.length,
          activityIndex,
          itemIndex,
        });
      });
    }
  });

  return flatItems;
};

const AcademyTextbookPreview: React.FC = () => {
  const { unitId, textbookId } = useParams<{ unitId: string; textbookId: string }>();
  const [unitData, setUnitData] = useState<UnitPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(0);
  const [responses, setResponses] = useState<Record<number, any>>({});
  const submitValidatorRef = useRef<(() => Promise<boolean> | boolean) | null>(null);
  const [hasSubmitValidator, setHasSubmitValidator] = useState(false);
  const navigate = useNavigate();
  const slugPrefix = getSlugPrefix();

  useEffect(() => {
    setStep(0);
    setResponses({});
    submitValidatorRef.current = null;
    setHasSubmitValidator(false);
  }, [unitId]);

  useEffect(() => {
    const fetchData = async () => {
      if (!unitId) return;
      setLoading(true);
      setError('');
      try {
        const res = await academyTextbookApi.previewUnit(Number(unitId));
        const data = res?.data || res;
        setUnitData(data?.data || data || null);
      } catch (e: any) {
        console.error('미리보기 로드 실패', e);
        const msg = e?.response?.data?.message || '미리보기 데이터를 불러오지 못했습니다.';
        setError(msg);
        setUnitData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [unitId]);

  const flatItems = useMemo(() => buildFlatItems(unitData), [unitData]);
  const totalItems = flatItems.length;
  const currentFlatItem = flatItems[step];
  const previousFlatItem = step > 0 ? flatItems[step - 1] : null;
  const CurrentActivityComponent = currentFlatItem ? ActivityComponents[currentFlatItem.activity.activityType] : null;
  const isComplete = step >= totalItems;
  const hasNextUnit = Boolean(unitData?.nextUnitId);
  const currentResponse = responses[step];

  const isPlainObject = (value: any) => {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  };

  useEffect(() => {
    submitValidatorRef.current = null;
    setHasSubmitValidator(false);
  }, [step]);

  const handleResponse = useCallback((activityIndex: number, response: any) => {
    setResponses((prev) => {
      const prevResponse = prev[activityIndex] || {};
      let nextResponse = response;

      if (isPlainObject(response)) {
        const { __replaceResponse, ...rest } = response as Record<string, any>;
        if (__replaceResponse) {
          nextResponse = rest;
        } else {
          nextResponse = { ...prevResponse, ...rest };
        }
      } else if (response === undefined) {
        nextResponse = prevResponse;
      }

      return {
        ...prev,
        [activityIndex]: nextResponse,
      };
    });
  }, []);

  const registerSubmitValidator = useCallback((validator: (() => Promise<boolean> | boolean) | null) => {
    submitValidatorRef.current = validator;
    setHasSubmitValidator((prev) => {
      const next = Boolean(validator);
      return prev === next ? prev : next;
    });
  }, []);

  const runSubmitValidation = async () => {
    if (isComplete) return true;
    if (submitValidatorRef.current) {
      const result = await Promise.resolve(submitValidatorRef.current());
      return result !== false;
    }
    return currentResponse?.isCorrect === true;
  };

  const isFlashCardActivity = currentFlatItem?.activity.activityType === 'FLASH_CARD';
  const previousIsFlashCardActivity = previousFlatItem?.activity.activityType === 'FLASH_CARD';
  const isSpellCheckActivity = currentFlatItem?.activity.activityType === 'SPELL_CHECK';
  const previousIsSpellCheckActivity = previousFlatItem?.activity.activityType === 'SPELL_CHECK';
  const isDictationActivity = currentFlatItem?.activity.activityType === 'DICTATION';
  const previousIsDictationActivity = previousFlatItem?.activity.activityType === 'DICTATION';
  const hidePrevButtonForFlashCard = !isComplete && isFlashCardActivity && !previousIsFlashCardActivity;
  const hidePrevButtonForSpellCheck = !isComplete && isSpellCheckActivity && !previousIsSpellCheckActivity;
  const hidePrevButtonForDictation = !isComplete && isDictationActivity && !previousIsDictationActivity;
  const hidePrevButton = hidePrevButtonForFlashCard || hidePrevButtonForSpellCheck || hidePrevButtonForDictation;

  const requiresSubmitValidation = hasSubmitValidator;
  const needsCorrectAnswer = !isComplete && !requiresSubmitValidation && currentResponse?.isCorrect !== true;

  const goDetail = () => {
    const base = slugPrefix || '';
    if (textbookId) {
      navigate(`${base}/academy/textbook/${textbookId}`);
      return;
    }
    navigate(`${base}/academy/textbook/list`);
  };

  return (
    <main className='flex flex-col flex-1 py-8 px-4 md:px-8'>
      <div className='flex items-center justify-between mb-6'>
        <div>
          <p className='text-sm text-gray-500'>교재 미리보기</p>
          <h1 className='heading1'>{unitData?.unitTitle || '유닛 미리보기'}</h1>
          <p className='text-gray-500 text-sm'>
            {unitData?.textbookTitle ? `${unitData.textbookTitle} · ` : ''}{unitData?.unitCategory || ''}
          </p>
        </div>
        <Button intent='secondary' onClick={goDetail}>목록으로</Button>
      </div>

      {loading && (
        <div className='p-6 bg-white rounded-xl border border-gray-100'>불러오는 중...</div>
      )}
      {!loading && error && (
        <div className='p-6 bg-white rounded-xl border border-gray-100 text-red-500'>{error}</div>
      )}
      {!loading && !error && totalItems === 0 && (
        <div className='p-6 bg-white rounded-xl border border-gray-100'>표시할 활동이 없습니다.</div>
      )}

      {!loading && !error && totalItems > 0 && (
        <section className='flex flex-col flex-1 bg-white rounded-xl border border-gray-100 shadow-sm'>
          <div className='flex justify-between items-center px-5 pt-5 pb-3 border-b border-gray-200'>
            <h2 className='text-lg font-bold'>{unitData?.unitCategory || unitData?.textbookTitle || '교재'}</h2>
            <span className='text-sm text-gray-500'>{isComplete ? totalItems : step + 1} / {totalItems}</span>
          </div>

          <div className='flex-1'>
            {isComplete ? (
              <Complete />
            ) : CurrentActivityComponent && currentFlatItem ? (
              <CurrentActivityComponent
                key={`item-${step}`}
                activity={{
                  ...currentFlatItem.activity,
                  items: SINGLE_ITEM_ACTIVITIES.includes(currentFlatItem.activity.activityType)
                    ? currentFlatItem.activity.items
                    : [currentFlatItem.item],
                }}
                unitData={unitData}
                onResponse={(response: any) => handleResponse(step, response)}
                existingResponse={responses[step]}
                isHomework={false}
                registerSubmitValidator={registerSubmitValidator}
              />
            ) : (
              <div className='flex justify-center items-center h-64 text-gray-500'>
                해당 활동 타입을 표시할 수 없습니다.
              </div>
            )}
          </div>

          <div className='flex gap-4 p-5 pb-3 w-full'>
            <Button
              intent='secondary'
              className='w-full'
              onClick={() => setStep((prev) => Math.max(0, isComplete ? totalItems - 1 : prev - 1))}
              disabled={(step === 0 && !isComplete) || hidePrevButton}
            >
              이전
            </Button>
            <Button
              className='w-full'
              onClick={async () => {
                if (isComplete) {
                  if (unitData?.nextUnitId) {
                    navigate(`${slugPrefix || ''}/academy/textbook/${textbookId}/unit/${unitData.nextUnitId}/preview`);
                    return;
                  }
                  goDetail();
                  return;
                }
                const canProceed = await runSubmitValidation();
                if (!canProceed) {
                  return;
                }
                setStep((prev) => Math.min(prev + 1, totalItems));
              }}
              disabled={needsCorrectAnswer}
            >
              {isComplete ? (hasNextUnit ? '다음 유닛' : '목록으로') : '다음'}
            </Button>
          </div>
        </section>
      )}
    </main>
  );
};

export default AcademyTextbookPreview;
